import { GAME_IDS, PROTOCOL_VERSION, type GameId, type RefusalReason } from '@ofc/shared';

/**
 * The socket layer, shared by every Organised Fun Centre game. Everything about
 * surviving a bad connection lives here: automatic reconnection, seat re-claiming,
 * sequence-gap detection and clock synchronisation.
 *
 * The screens above it never see a disconnect — they see a status flag and a game
 * state that is always the server's.
 */

/**
 * - `refused`: the room said no (full, removed, name taken, out of date). Nothing
 *   more is tried; `currentRefusal()` says why.
 * - `unreachable`: still trying, but it has been a while, so the screen should say
 *   so and offer a way out rather than a quietly blinking dot.
 */
export type ConnStatus = 'connecting' | 'live' | 'reconnecting' | 'unreachable' | 'refused' | 'closed';

export interface Refusal {
  reason: RefusalReason;
  message: string;
}

/** A game's room, or the game night above it. */
export type ConnectionKind = GameId | 'lounge';

export interface Identity {
  game: ConnectionKind;
  roomCode: string;
  playerId: string;
  token: string;
  /** When the room last welcomed us, so the front door can offer the latest one back. */
  seenAt?: number;
}

/**
 * The minimum every game's message unions must satisfy. Each game passes its own
 * union as the type parameter; the connection only ever inspects these fields.
 */
export interface MessageLike {
  t: string;
}

export interface ConnectionHandlers<S extends MessageLike> {
  onMessage(msg: S): void;
  onStatus(status: ConnStatus): void;
  /** Fired when the server assigns or confirms a seat. */
  onIdentity(identity: Identity): void;
}

export interface ConnectionOptions<S extends MessageLike> {
  game: ConnectionKind;
  roomCode: string;
  name: string;
  handlers: ConnectionHandlers<S>;
  /** Present when signed in; the server then uses the account's name and colours. */
  sessionToken?: string;
  /** A big shared screen: watches, holds no seat, remembers no identity. */
  spectator?: boolean;
  /** One of the audience: watches like a big screen, and may vote. */
  audience?: boolean;
}

/** What a reaction looks like once it reaches the page. */
export interface ReactionEvent {
  playerId: string;
  emoji: string;
}

/** Fired on `window` for every reaction that arrives, from whichever room. */
export const REACTION_EVENT = 'ofc:reaction';

/** Fired on `window` with `{ count }` whenever the audience grows or shrinks. */
export const AUDIENCE_EVENT = 'ofc:audience';
/** Fired on `window` with what the audience made of the vote just closed. */
export const AUDIENCE_RESULT_EVENT = 'ofc:audience-result';

/** This phone's audience id, kept so a reconnect is still one vote. */
function audienceId(): string {
  try {
    const known = localStorage.getItem('ofc:audience-id');
    if (known) return known;
    const fresh = crypto.randomUUID();
    localStorage.setItem('ofc:audience-id', fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * The room this phone is playing in right now. There is only ever one, and the
 * shared widgets — the reaction bar, the pause button — reach it through here
 * rather than every game hook having to hand them a way in.
 */
let activeRoom: GameConnection<MessageLike, MessageLike> | null = null;

/** Sends a message every room understands to the room this phone is in. */
export function sendToRoom(
  msg: { t: 'react'; emoji: string } | { t: 'pause' } | { t: 'resume' } | { t: 'skip' } | { t: 'endGame' },
): void {
  activeRoom?.send(msg);
}

/** Why the room this phone tried last turned it away, if it did. */
let lastRefusal: Refusal | null = null;
export function currentRefusal(): Refusal | null {
  return lastRefusal;
}

/** Asks the app to play under a different name, after a room said the name is taken. */
export const RENAME_EVENT = 'ofc:rename';

/** Try the room again now, from a "can't reach the room" banner. */
export function retryRoom(): void {
  activeRoom?.retry();
}

const PING_INTERVAL_MS = 15_000;
const MAX_BACKOFF_MS = 5_000;
/** After this long without getting back in, the screen says so. */
export const UNREACHABLE_AFTER_MS = 90_000;
/** And from then on it tries less often, so a dead room is not hammered. */
const SLOW_RETRY_MS = 15_000;

/**
 * Messages sent while the connection is down wait here and go once it is back,
 * rather than vanishing. Bounded, and stale ones are dropped: an answer from two
 * minutes ago is no longer an answer to anything.
 *
 * Each one remembers where the room was when it was sent (its sequence number).
 * If the room has moved on by the time the phone is back — the next question, the
 * next round — it is dropped rather than sent: an answer to question 3 must never
 * land on question 4.
 */
export const OUTBOX_LIMIT = 20;
export const OUTBOX_MAX_AGE_MS = 60_000;

export interface Queued<C> {
  msg: C;
  at: number;
  /** Where the room was when it was sent: its stage, or its sequence if it has none. */
  seq: number;
}

/** Adds to the outbox, dropping the oldest once it is full. Reactions never wait. */
export function enqueue<C extends MessageLike>(box: Queued<C>[], msg: C, now: number, seq = 0): Queued<C>[] {
  if (msg.t === 'react') return box;
  const next = [...box, { msg, at: now, seq }];
  return next.length > OUTBOX_LIMIT ? next.slice(next.length - OUTBOX_LIMIT) : next;
}

/**
 * What is still worth sending once the connection is back: recent, and sent while
 * the room was where it is now. `roomSeq` is the sequence the room welcomed us
 * back at; without one (an older room) only the age counts.
 */
export function drain<C>(box: Queued<C>[], now: number, roomSeq?: number): C[] {
  return box
    .filter((q) => now - q.at <= OUTBOX_MAX_AGE_MS)
    .filter((q) => roomSeq === undefined || q.seq === roomSeq)
    .map((q) => q.msg);
}

/** Seats are per game, so a player can hold one in a movie room and a quiz room. */
function identityKey(game: ConnectionKind): string {
  return `ofc:identity:${game}`;
}

export function loadIdentity(game: ConnectionKind, roomCode: string): Identity | null {
  try {
    const raw = localStorage.getItem(identityKey(game));
    if (!raw) return null;
    const identity = JSON.parse(raw) as Identity;
    return identity.roomCode === roomCode ? identity : null;
  } catch {
    return null;
  }
}

/** The server forgets a room after six quiet hours, so there is no point offering one older. */
const RECENT_ROOM_MS = 6 * 60 * 60 * 1000;

/**
 * The room you were in most recently, if it is recent enough to still exist.
 *
 * One identity is kept per game, so this reads all of them and takes the newest.
 * Entries written before `seenAt` existed have no time on them and are skipped —
 * nothing can say how old they are.
 */
export function recentRoom(now = Date.now()): { game: GameId; roomCode: string } | null {
  let best: Identity | null = null;
  for (const game of GAME_IDS) {
    try {
      const raw = localStorage.getItem(identityKey(game));
      if (!raw) continue;
      const identity = JSON.parse(raw) as Identity;
      if (!identity.seenAt || now - identity.seenAt > RECENT_ROOM_MS) continue;
      if (identity.game === 'lounge') continue;
      if (!best || identity.seenAt > (best.seenAt ?? 0)) best = identity;
    } catch {
      // An unreadable entry is just not offered.
    }
  }
  return best && best.game !== 'lounge' ? { game: best.game, roomCode: best.roomCode } : null;
}

function saveIdentity(identity: Identity): void {
  try {
    localStorage.setItem(identityKey(identity.game), JSON.stringify(identity));
  } catch {
    // Private mode or blocked storage: the session still works, it just cannot
    // survive a page reload.
  }
}

export class GameConnection<C extends MessageLike, S extends MessageLike> {
  private ws: WebSocket | null = null;
  private status: ConnStatus = 'connecting';
  private attempt = 0;
  private closedByUs = false;
  private refused = false;
  /** When the connection was last live, or first tried; for "unreachable". */
  private lastLive = Date.now();
  private outbox: Queued<C>[] = [];

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  /** Highest sequence number applied. A gap means a message was missed. */
  private lastSeq = 0;
  /** The room's stage as last heard, from rooms that send one. */
  private lastStage: number | null = null;
  private resyncPending = false;

  /** serverTime - clientTime, from the lowest-latency ping sample seen. */
  private clockOffset = 0;
  private bestRtt = Infinity;

  private identity: Identity | null;
  private readonly opts: ConnectionOptions<S>;

  constructor(opts: ConnectionOptions<S>) {
    this.opts = opts;
    this.identity = opts.spectator || opts.audience ? null : loadIdentity(opts.game, opts.roomCode);
  }

  start(): void {
    if (this.opts.game !== 'lounge' && !this.opts.spectator && !this.opts.audience) {
      activeRoom = this as unknown as GameConnection<MessageLike, MessageLike>;
    }
    lastRefusal = null;
    this.lastLive = Date.now();
    this.open();
    // A phone that has been locked, or moved from wifi to data, usually has a dead
    // socket that never fired a close event. Both of these catch that.
    window.addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
  }

  stop(): void {
    if ((activeRoom as unknown) === this) activeRoom = null;
    this.closedByUs = true;
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
    this.clearTimers();
    this.ws?.close(1000, 'left');
    this.ws = null;
    this.setStatus('closed');
  }

  /** Sends now, or once the connection is back. */
  send(msg: C): void {
    if (this.ws?.readyState !== WebSocket.OPEN || this.status !== 'live') {
      if (!this.refused && !this.closedByUs) this.outbox = enqueue(this.outbox, msg, Date.now(), this.lastStage ?? this.lastSeq);
      return;
    }
    this.ws.send(JSON.stringify(msg));
  }

  /** Whether anything is waiting to go. */
  get waiting(): number {
    return this.outbox.length;
  }

  retry(): void {
    if (this.refused) return;
    this.lastLive = Date.now();
    this.reconnectNow();
  }

  /** Server time now, corrected for this device's clock skew. */
  now(): number {
    return Date.now() + this.clockOffset;
  }

  // -------------------------------------------------------------------------

  /**
   * Sends an envelope message the connection owns (hello, ping, resync) rather
   * than one from a game's own union.
   */
  private sendRaw(msg: { t: string } & Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  private wake = (): void => {
    if (document.visibilityState === 'hidden') return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Looks open, but may be a zombie. A ping proves it either way.
      this.sendRaw({ t: 'ping', clientTime: Date.now() });
      return;
    }
    this.reconnectNow();
  };

  private open(): void {
    if (this.closedByUs) return;
    this.clearTimers();

    const { game, roomCode } = this.opts;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url =
      game === 'lounge'
        ? `${proto}//${location.host}/ws/lounge?code=${encodeURIComponent(roomCode)}`
        : `${proto}//${location.host}/ws?room=${encodeURIComponent(roomCode)}&game=${game}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    if (this.status !== 'unreachable') this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    ws.onopen = () => {
      this.attempt = 0;
      this.sendRaw({
        t: 'hello',
        protocol: PROTOCOL_VERSION,
        name: this.opts.name,
        playerId: this.identity?.playerId,
        token: this.identity?.token,
        sessionToken: this.opts.sessionToken,
        ...(this.opts.spectator ? { spectator: true } : {}),
        ...(this.opts.audience ? { audience: true, audienceId: audienceId() } : {}),
      });
      this.sendRaw({ t: 'ping', clientTime: Date.now() });
      this.pingTimer = setInterval(
        () => this.sendRaw({ t: 'ping', clientTime: Date.now() }),
        PING_INTERVAL_MS,
      );
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      let msg: S;
      try {
        msg = JSON.parse(event.data) as S;
      } catch {
        return;
      }
      this.receive(msg);
    };

    ws.onclose = () => {
      if (this.ws === ws) this.scheduleReconnect();
    };

    ws.onerror = () => {
      // `close` always follows, so reconnection is handled there.
    };
  }

  private receive(msg: S): void {
    // The connection only understands the envelope; the rest is the game's business.
    const env = msg as unknown as {
      t: string;
      seq?: number;
      stage?: number;
      clientTime?: number;
      serverTime?: number;
      you?: string;
      token?: string;
    };

    // The room has said no. Retrying would only be told no again.
    if (env.t === 'refused') {
      const refusal = msg as unknown as Refusal;
      lastRefusal = { reason: refusal.reason, message: refusal.message };
      this.refused = true;
      this.outbox = [];
      this.clearTimers();
      this.setStatus('refused');
      return;
    }

    if (env.t === 'pong') {
      const rtt = Date.now() - (env.clientTime ?? 0);
      // The lowest round trip gives the least distorted offset estimate.
      if (rtt < this.bestRtt) {
        this.bestRtt = rtt;
        this.clockOffset = (env.serverTime ?? 0) - ((env.clientTime ?? 0) + rtt / 2);
      }
      return;
    }

    // Decoration, not state: handed to whatever is drawing reactions, and kept
    // well away from the sequence check.
    if (env.t === 'reaction') {
      const detail = msg as unknown as ReactionEvent;
      window.dispatchEvent(new CustomEvent<ReactionEvent>(REACTION_EVENT, { detail }));
      return;
    }

    // The audience's size and verdicts: decoration too, for whoever is drawing them.
    if (env.t === 'audience' || env.t === 'audienceResult') {
      const name = env.t === 'audience' ? AUDIENCE_EVENT : AUDIENCE_RESULT_EVENT;
      window.dispatchEvent(new CustomEvent(name, { detail: msg }));
      return;
    }

    if (env.t === 'welcome' && (this.opts.spectator || this.opts.audience)) {
      this.lastSeq = env.seq ?? 0;
      this.resyncPending = false;
      this.setStatus('live');
      this.opts.handlers.onMessage(msg);
      return;
    }

    if (env.t === 'welcome') {
      this.identity = {
        game: this.opts.game,
        roomCode: this.opts.roomCode,
        playerId: env.you ?? '',
        token: env.token ?? '',
        seenAt: Date.now(),
      };
      saveIdentity(this.identity);
      this.opts.handlers.onIdentity(this.identity);
      // Where the room is now, to judge the outbox by: its stage when it has one,
      // so somebody else joining or leaving does not throw away your answer.
      const roomSeq = typeof env.stage === 'number' ? env.stage : env.seq;
      this.lastSeq = env.seq ?? 0;
      this.lastStage = typeof env.stage === 'number' ? env.stage : null;
      this.resyncPending = false;
      this.lastLive = Date.now();
      this.setStatus('live');
      this.opts.handlers.onMessage(msg);
      // Anything sent while we were away goes now, after the room knows who we are.
      const pending = drain(this.outbox, Date.now(), roomSeq);
      this.outbox = [];
      for (const queued of pending) this.ws?.send(JSON.stringify(queued));
      return;
    }

    if (typeof env.seq === 'number') {
      // A full snapshot is safe to apply from any sequence position.
      const isSnapshot = env.t === 'state';
      if (!isSnapshot && env.seq !== this.lastSeq + 1) {
        this.requestResync();
        // Behind us: a repeat, already applied.
        if (env.seq <= this.lastSeq) return;
        // Ahead of us: something was missed, but this is still the latest word,
        // and some messages say what nothing else will ("go to this game", "a new
        // question"). Apply it; the snapshot on its way fills in the rest.
      }
      this.lastSeq = env.seq;
      if (typeof env.stage === 'number') this.lastStage = env.stage;
      if (isSnapshot) this.resyncPending = false;
    }

    this.opts.handlers.onMessage(msg);
  }

  private requestResync(): void {
    if (this.resyncPending) return;
    this.resyncPending = true;
    this.sendRaw({ t: 'resync' });
  }

  private scheduleReconnect(): void {
    if (this.closedByUs || this.refused) return;
    this.clearTimers();
    if (this.status === 'live') this.lastLive = Date.now();
    const unreachable = Date.now() - this.lastLive > UNREACHABLE_AFTER_MS;
    this.setStatus(unreachable ? 'unreachable' : 'reconnecting');

    const delay = unreachable
      ? SLOW_RETRY_MS
      : Math.min(MAX_BACKOFF_MS, 200 * 2 ** this.attempt) + Math.random() * 200;
    this.attempt++;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private reconnectNow(): void {
    if (this.closedByUs || this.refused) return;
    this.attempt = 0;
    this.open();
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  private setStatus(status: ConnStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.opts.handlers.onStatus(status);
  }
}
